import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/mocks/movies";
import { typeMeta } from "@/lib/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      setActive((index + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (paused || items.length < 2) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (!items.length) return null;

  const current = items[active];

  return (
    <section className="w-full px-4 pt-4 md:px-8 md:pt-6" aria-label="焦点影视推荐">
      <div
        className="relative h-[470px] w-full overflow-hidden rounded-xl bg-foreground-900 sm:h-[520px] md:h-[560px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0].clientX;
        }}
        onTouchEnd={(event) => {
          if (touchStartX.current === null) return;
          const delta = event.changedTouches[0].clientX - touchStartX.current;
          if (Math.abs(delta) > 48) goTo(active + (delta < 0 ? 1 : -1));
          touchStartX.current = null;
        }}
      >
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.backdrop}
              alt={`${item.title} 剧照`}
              title={`${item.title} - 蜜桃33焦点推荐`}
              className="h-full w-full object-cover object-center"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-foreground-950/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-foreground-950/30" />

        <div className="relative flex h-full flex-col justify-between p-6 md:p-10 lg:p-12">
          <div className="flex items-center gap-3">
            <span className="rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-background-100 backdrop-blur">
              Today&apos;s Pick
            </span>
            <span className="text-[11px] tracking-wider text-background-200/70">
              编辑精选 · 第 {String(active + 1).padStart(2, "0")} 期
            </span>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div className="max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                  {typeMeta[current.type].label}
                </span>
                {current.genres.slice(0, 3).map((genre) => (
                  <span
                    key={genre}
                    className="rounded-md border border-background-50/25 px-2.5 py-1 text-[11px] text-background-100/85"
                  >
                    {genre}
                  </span>
                ))}
              </div>

              <h2 className="mt-4 font-heading text-3xl font-bold leading-tight tracking-wide text-background-50 sm:text-4xl md:text-5xl lg:text-[56px]">
                {current.title}
              </h2>
              <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-background-200/60">
                {current.originalTitle}
              </p>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
                {current.tagline}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-200/80">
                <span className="flex items-center gap-1 text-accent-300">
                  <i className="ri-star-fill text-[11px]" />
                  <span className="font-semibold text-accent-200">{current.rating.toFixed(1)}</span>
                  <span className="text-background-300/60">虚构评分</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" /> {current.year}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line" /> {current.region}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" /> {current.episodes}
                </span>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2 sm:mt-7 sm:gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 sm:h-11 sm:gap-2 sm:px-6 sm:text-sm"
                >
                  <i className="ri-play-circle-line text-base" />
                  查看详情
                </Link>
                <Link
                  to={`/category/${current.type}`}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/35 px-4 text-xs text-background-100 transition-colors hover:bg-background-50/10 sm:h-11 sm:gap-2 sm:px-6 sm:text-sm"
                >
                  浏览{typeMeta[current.type].label}
                  <i className="ri-arrow-right-line text-base" />
                </Link>
              </div>
            </div>

            <div className="hidden gap-2.5 lg:flex">
              {items.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`切换到 ${item.title}`}
                  onClick={() => goTo(index)}
                  className={`group relative h-28 w-20 cursor-pointer overflow-hidden rounded-md border-2 transition-all ${
                    index === active
                      ? "border-accent-400"
                      : "border-background-50/20 opacity-60 hover:opacity-100"
                  }`}
                >
                  <img
                    src={item.cover}
                    alt={item.title}
                    className="h-full w-full object-cover object-top"
                  />
                  <span className="absolute inset-x-0 bottom-0 line-clamp-1 bg-foreground-950/70 px-1 py-1 text-[10px] text-background-50">
                    {item.title}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {items.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`第 ${index + 1} 张`}
                  onClick={() => goTo(index)}
                  className={`h-1.5 cursor-pointer rounded-full transition-all ${
                    index === active
                      ? "w-8 bg-accent-400"
                      : "w-3 bg-background-50/35 hover:bg-background-50/60"
                  }`}
                />
              ))}
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              <button
                type="button"
                aria-label="上一张"
                onClick={() => goTo(active - 1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-100 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-left-s-line text-base" />
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => goTo(active + 1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-100 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-right-s-line text-base" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}